// Models → Providers & keys: what the gateway's GET /providers/overview says about each provider and its keys, in
// words (UI rework 2026-11-02, gateway docs/models-providers-tiers.md). A key belongs to a provider and serves every
// model that provider runs; whoever owns it pays the provider. Whitelabel mirrors this in lib/provider-keys.ts.

export const OWNER_LABELS = { user: 'You', tenant: 'Your organization', platform: 'BlueBear' };
// How the others line names each owner mid-sentence ("Also runs on BlueBear's key").
export const OWNER_POSSESSIVE = { user: 'your', tenant: "your organization's", platform: "BlueBear's" };

export function ownerLabel(ownerType, labels = OWNER_LABELS) {
  return labels[ownerType] ?? ownerType ?? '-';
}

/** Who is looking: decides which providers take a key or a model here (the gateway checks the same). */
export function viewerFrom(user) {
  const role = user?.role;
  const isPlatformAdmin = role === 'bluebear_admin' || (role === 'admin' && Boolean(user?.isBluebearEmployee ?? user?.is_bluebear_employee));
  const tenantId = user?.tenant_id ?? user?.tenantId ?? null;
  const realTenant = Boolean(tenantId) && tenantId !== '00000000-0000-0000-0000-000000000000';
  return {
    userId: user?.id ?? null,
    isPlatformAdmin,
    isTenantAdmin: realTenant && (role === 'admin' || role === 'tenant') && !isPlatformAdmin,
  };
}

/** Whose keys the caller may add to this provider: anyone their own on BlueBear's or their own provider. */
export function keyOwnerChoices(provider, viewer) {
  const owner = provider?.owner_type;
  if (owner === 'user') return provider.owner_id === viewer.userId ? ['user'] : [];
  if (owner === 'tenant') return viewer.isTenantAdmin ? ['tenant'] : [];
  const choices = [];
  if (viewer.isPlatformAdmin) choices.push('platform');
  if (viewer.isTenantAdmin) choices.push('tenant');
  choices.push('user');
  return choices;
}

/** The provider itself (its address, its models) is changed by its owner: the platform's by platform admins. */
export function canManageProvider(provider, viewer) {
  if (provider?.owner_type === 'user') return provider.owner_id === viewer.userId;
  if (provider?.owner_type === 'tenant') return viewer.isTenantAdmin;
  return provider?.owner_type === 'platform' && viewer.isPlatformAdmin;
}

/** Whose new provider the caller may add: their own; their organization's (tenant admins); BlueBear's (platform admins). */
export function providerOwnerChoices(viewer) {
  if (viewer.isPlatformAdmin) return ['platform', 'user'];
  if (viewer.isTenantAdmin) return ['tenant', 'user'];
  return ['user'];
}

/** A key is changed here by whoever sees it in full (the overview sends no one else's). */
export function canManageKey(key, viewer) {
  if (key?.owner_type === 'platform') return viewer.isPlatformAdmin;
  if (key?.owner_type === 'tenant') return viewer.isTenantAdmin;
  return key?.owner_type === 'user';
}

function clock(iso, now) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const time = at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return at.toDateString() === now.toDateString() ? time : `${at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
}

function refusedCode(text) {
  const match = /\((\d{3})\)/.exec(String(text ?? ''));
  return match ? ` (${match[1]})` : '';
}

/** A key's health in words: on, off, resting after the provider refused it, or resting on some models only. */
export function keyStatus(key, now = new Date()) {
  if (key?.status === 'off') return { tone: 'off', label: 'Off', detail: 'Switched off: nothing runs on it.' };
  if (key?.status === 'resting') {
    return {
      tone: 'error',
      label: 'Resting',
      detail: `The provider refused this key${refusedCode(key.last_error)}. Check it or replace it; it is tried again at ${clock(key.resting_until, now)}.`,
    };
  }
  if (key?.status === 'resting_models') {
    const rests = key.resting_models ?? [];
    const latest = rests.reduce((max, r) => (new Date(r.until) > new Date(max) ? r.until : max), rests[0]?.until);
    return {
      tone: 'warn',
      label: `Resting on ${rests.length} ${rests.length === 1 ? 'model' : 'models'}`,
      detail: `${rests.map((r) => r.model_name).join(', ')}: the provider refused access. Tried again at ${clock(latest, now)}.`,
    };
  }
  return { tone: 'ok', label: 'Active', detail: '' };
}

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/** Today's use of a key (the UTC day, list price, the daily budget's unit) and how full its budget is. */
export function todayUse(key) {
  const spend = Number(key?.today_spend_usd || 0);
  const calls = Number(key?.calls_today || 0);
  const budget = key?.daily_budget_usd != null && key.daily_budget_usd !== '' ? Number(key.daily_budget_usd) : null;
  if (budget) return { text: `${money(spend)} of ${money(budget)} budget`, ratio: Math.min(1, spend / budget) };
  if (calls) return { text: `${money(spend)} · ${calls} ${calls === 1 ? 'call' : 'calls'}`, ratio: null };
  return { text: 'No calls today', ratio: null };
}

export function lastUsedText(iso, now = new Date()) {
  if (!iso) return 'Never';
  const at = new Date(iso);
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60000);
  if (Number.isNaN(minutes)) return 'Never';
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h ago`;
  if (minutes < 48 * 60) return 'Yesterday';
  return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** A model's name on a chip: its name, with its id when another model of the provider has the same name. */
export function modelChipName(model, models) {
  const name = model.model_name || model.model_string;
  const same = (models ?? []).filter((m) => (m.model_name || m.model_string) === name).length;
  return same > 1 && model.model_string ? `${name} (${model.model_string})` : name;
}

/** The models line: the first few by name, then how many more. */
export function modelsSummary(models, max = 3) {
  const names = (models ?? []).filter((m) => m.is_active !== false).map((m) => m.model_name || m.model_string);
  return { shown: names.slice(0, max), more: Math.max(0, names.length - max), total: names.length };
}

/** "Serves": every model the provider runs (a key serves them all). */
export function servesText(provider) {
  const total = modelsSummary(provider?.models, 0).total;
  if (total === 0) return 'no models yet';
  return total === 1 ? '1 model' : `all ${total} models`;
}

/** The filter chips: all, or the providers holding keys of one owner (shown in full or counted). */
export function filterProviders(providers, filter) {
  if (!filter || filter === 'all') return providers ?? [];
  return (providers ?? []).filter((p) => (p.keys ?? []).some((k) => k.owner_type === filter) || Number(p.other_keys?.[filter] ?? 0) > 0);
}

/** Keys others hold on this provider, said once ("BlueBear's key", "2 of your organization's keys"). */
export function otherKeysText(otherKeys, labels = OWNER_LABELS) {
  return Object.entries(otherKeys ?? {})
    .filter(([, n]) => Number(n) > 0)
    .map(([owner, n]) => {
      const who = OWNER_POSSESSIVE[owner] ?? `${ownerLabel(owner, labels)}'s`;
      return Number(n) === 1 ? `${who} key` : `${n} of ${who} keys`;
    });
}

/** The address a key write goes to: the platform's keys through the admin console, the rest as their owner. */
export function keyPath(key, suffix = '') {
  if (key.owner_type === 'platform') return `/admin/catalog/credentials/${key.id}${suffix}`;
  return `/providers/credentials/${key.id}${suffix}?owner_type=${key.owner_type}`;
}

/** A provider's own writes (add, remove, its models): the platform's through the admin console, the rest as their owner. */
export function providerPath(ownerType, providerId = null, suffix = '') {
  if (ownerType === 'platform') return `/admin/catalog/providers${providerId ? `/${providerId}` : ''}${suffix}`;
  return `/providers${providerId ? `/${providerId}` : ''}${suffix}?owner_type=${ownerType}`;
}

/** A model link (offering) of a provider: removing one takes the model off that provider. */
export function offeringPath(ownerType, offeringId) {
  if (ownerType === 'platform') return `/admin/catalog/offerings/${offeringId}`;
  return `/providers/offerings/${offeringId}?owner_type=${ownerType}`;
}

export function addKeyPath(providerId, ownerType) {
  if (ownerType === 'platform') return `/admin/catalog/providers/${providerId}/credentials`;
  return `/providers/${providerId}/credentials?owner_type=${ownerType}`;
}

/** What a new key's form sends (snake_case; the secret only on create or rotate). */
export function keyPayload(form) {
  const number = (v) => (v === '' || v == null ? null : Number(v));
  return {
    alias: String(form.alias ?? '').trim() || null,
    ...(form.secret ? { secret: String(form.secret).trim() } : {}),
    daily_budget_usd: number(form.dailyBudgetUsd),
    rate_limit_rpm: number(form.rateLimitRpm),
  };
}

/** Presets a person can add (plans have their own panel). */
export function addablePresets(presets) {
  return (presets ?? []).filter((p) => p.kind !== 'subscription');
}

// ── Connect a provider → Test connection (gateway POST /providers/probe) ──

export const AUTH_MODES = [
  ['auto', 'Find out (recommended)'],
  ['bearer', 'Authorization: Bearer'],
  ['header', 'Header (name it)'],
  ['query', 'Query parameter'],
  ['basic', 'Basic'],
  ['none', 'None'],
];

/** How the key is sent, as the form says (null = let the test find out). */
export function authFromForm(form) {
  const mode = form.authMode || 'auto';
  if (mode === 'auto') return null;
  if (mode === 'header') return { type: 'header', header: String(form.authHeader || '').trim() || 'x-api-key' };
  if (mode === 'query') return { type: 'query', param: String(form.authParam || '').trim() || 'key' };
  return { type: mode };
}

/** The probe's body: a preset or any address, the key, how it is sent. */
export function probePayload(form) {
  const preset = form.preset && form.preset !== 'custom' ? form.preset : null;
  const auth = authFromForm(form);
  return {
    ...(preset ? { preset } : {}),
    ...(String(form.baseUrl ?? '').trim() ? { base_url: String(form.baseUrl).trim() } : {}),
    ...(String(form.secret ?? '').trim() ? { api_key: String(form.secret).trim() } : {}),
    ...(auth ? { auth } : {}),
    ...(String(form.model ?? '').trim() ? { model: String(form.model).trim() } : {}),
  };
}

/** The provider to save: the preset (or any address), with what the test found when it found a format. */
export function connectPayload(form, probe) {
  const preset = form.preset && form.preset !== 'custom' ? form.preset : null;
  const found = probe?.endpoint_type ? probe : null;
  const formats = found ? Object.entries(found.formats || {}).filter(([, r]) => r?.ok).map(([f]) => f) : [];
  const auth = found?.auth?.accepted
    ? { type: found.auth.type, ...(found.auth.header ? { header: found.auth.header } : {}), ...(found.auth.param ? { param: found.auth.param } : {}) }
    : authFromForm(form);
  return {
    ...(preset ? { preset } : { kind: 'custom' }),
    ...(String(form.name ?? '').trim() ? { name: String(form.name).trim() } : {}),
    ...(String(form.baseUrl ?? '').trim() ? { base_url: String(form.baseUrl).trim() } : {}),
    ...(formats.length ? { formats, default_format: found.endpoint_type } : {}),
    ...(auth ? { auth } : {}),
  };
}

const FORMAT_WORDS = { anthropic_messages: 'Anthropic Messages', openai_chat: 'Chat Completions', openai_responses: 'Responses' };

/** What the test found, line by line, in words. */
export function probeLines(probe, { hasKey = true } = {}) {
  if (!probe) return [];
  const lines = [];
  lines.push({ label: 'Reachable', ok: probe.reachable, text: probe.reachable ? `Yes${probe.public_address ? ' · public address' : ' · local address (BlueBear only)'}` : 'No' });
  if (!probe.reachable) return lines;
  const a = probe.auth;
  lines.push({
    label: 'Key',
    ok: Boolean(a?.accepted),
    text: a?.accepted
      ? `Accepted as ${a.type === 'bearer' ? 'Authorization: Bearer' : a.type === 'header' ? `header ${a.header}` : a.type === 'query' ? `query ?${a.param}=` : a.type}`
      : hasKey ? 'Refused every way it was sent' : 'None given: it wants one',
  });
  lines.push({
    label: 'Formats it speaks',
    ok: Boolean(probe.endpoint_type),
    text: Object.entries(probe.formats || {}).filter(([, r]) => r).map(([f, r]) => `${FORMAT_WORDS[f] || f} ${r.ok ? '✓' : `✕${r.status ? ` ${r.status}` : ''}`}`).join(' · ') || 'not tried',
  });
  if (probe.endpoint_type) lines.push({ label: 'Endpoint type', ok: true, text: `${FORMAT_WORDS[probe.endpoint_type]} · the default format for its models` });
  if (probe.streaming !== null || probe.tools !== null) {
    lines.push({ label: 'Streaming · tools', ok: probe.streaming !== false && probe.tools !== false, text: `Streaming ${probe.streaming ? '✓' : '✕'} · tool calls ${probe.tools ? '✓' : '✕'}` });
  }
  return lines;
}

/** The models the server lists, each matched to the catalog (an offering can be added for those). */
export function listedModels(probe, catalog = []) {
  const byString = new Map(catalog.map((m) => [m.model_string, m]));
  return (probe?.models || []).map((m) => {
    const known = byString.get(m.id) || null;
    return { ...m, catalogId: known?.id ?? null, catalogName: known ? known.model_type_name || known.type_name || known.model_string : null };
  });
}
